import { BaseComponent } from './base-component';
import { ModelRegistry } from './model-registry';
import { Logger } from './logger';
import { Retry, Retryable, RetryFailure } from './retry';
import { NavigationStart } from '@angular/router';
import { Component, Injectable } from '@angular/core';
import { NgZone, ApplicationRef, Optional } from '@angular/core';
import { TransferState } from '@angular/platform-browser';
import { Title, Meta } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Head } from './head';
import { Redirection } from './redirection';
import { LoggerService } from './logger.service';
import { Shell } from './shell';

@Injectable()
export class PageService {
    constructor(
        public zone : NgZone,
        public stateTransfer : TransferState,
        public appRef : ApplicationRef,
        public title : Title,
        public meta : Meta,
        public router : Router,
        public head : Head,
        public redirection : Redirection,
        public logger : LoggerService,
        public retry : Retry,
        public shell : Shell
    ) {
    }
    
    public page : PageComponent;
}

export function Page(name : string) {
    return (target : any) => {
        Reflect.defineMetadata('page:id', name, target);
    }
}

export function Transfer() {
    return (target : any, propertyKey : string) => {
        Reflect.defineMetadata('state-transfer:enabled', true, target, propertyKey);
    }
}

export interface StandardMeta {
    description? : string;
    keywords? : string;
    author? : string;
}

/**
 * Represents a component which is a page (or dialog). Such components are singletons, and are 
 * typically the center component of a page. Implements server-to-browser state transfer.
 */
@Component({ 
    template: ''
})
export class PageComponent extends BaseComponent {
    constructor(
        private __pageService : PageService
    ) {
        super();
    }

    public async handleNotFound<T>(promise : Promise<T>): Promise<T> {
        let result : T;
        try {
            return await promise;
        } catch (e) {
            if (e.error == 'not-found') {
                this.__pageService.router.navigate([`/404/${this.__pageService.router.routerState.snapshot.url}`]);
                return null;
            }

            throw e;
        }
    }

    restoreState(data : any) {
        if (!data)
            return;
        
        this._hasServerState = true;
        for (let key of Object.keys(data)) {
            this[key] = this.inflate(data[key]);
        }
    }

    /**
     * Whether a load error has been triggered or not. Used by <load-guard> to display an error when page data cannot be retrieved from the 
     * API service.
     */
    public loadError : boolean = false;

    /**
     * Load data with the standard page data retry policy, including triggering `loadError` in the case 
     * where the data cannot be obtained due to a typically retryable reason.
     * 
     * @param f 
     * @param onRetry 
     */
    protected async loadData<T>(f : Retryable<T>, onRetry = null): Promise<T> {
        try {
            return await this.__pageService.retry.standard(f, onRetry);
        } catch (e) {
            if (e instanceof RetryFailure)
                this.onLoadError(e);
            throw e;
        }
    }

    /**
     * Call this when an error occurs while loading data from the API. This will trigger any <load-guard> elements on the page to display an error.
     * @param e 
     */
    protected onLoadError(e) {
        Logger.error(`Error loading data for page ${this.constructor.name}`, e);
        this.loadError = true;
    }

    public set pageTitle(value : string) {
        if (!value)
            this.__pageService.title.setTitle(`TYT.com`);
        else
            this.__pageService.title.setTitle(`${value} - TYT.com`);
    }

    protected get meta() { return this.__pageService.meta; }
    protected get shell() { return this.__pageService.shell; }
    protected get router() { return this.__pageService.router; }

    public setMeta(meta : StandardMeta) {
        if (meta.description) this.meta.updateTag({ content: meta.description }, 'name=description');
        if (meta.keywords) this.meta.updateTag({ content: meta.keywords }, 'name=keywords');
        if (meta.author) this.meta.updateTag({ content: meta.author }, 'name=author');
    }

    private deflate(obj : any) {
        if (obj instanceof Array)
            return obj.map(x => this.deflate(x));
        
        if (obj !== null && obj !== undefined && typeof obj === 'object') {
            return {
                constructor: obj.constructor.name,
                data: JSON.stringify(obj)
            }
        }

        return obj;
    }

    private inflate(obj : any) {
        if (obj instanceof Array)
            return obj.map(x => this.inflate(x));
        
        if (obj !== null && obj !== undefined && typeof obj === 'object') {
            if (obj.constructor === 'Object') {
                return JSON.parse(obj.data);
            } else {
                return ModelRegistry.inflate(obj.constructor, JSON.parse(obj.data));
            }
        }

        return obj;
    }

    protected addHeadLink(rel : string, type : string, href : string, title : string = null) {
        let link = this.__pageService.head.addLink(rel, type, href, title);
        let self = this;

        this.subscribe(<any>{
            subscribe(...args) {
                return {
                    unsubscribe() {
                        self.__pageService.head.removeLink(link);
                    }
                }
            }
        }, () => {});
    }

    getState() {
        let state = {};

        for (let key of Object.keys(this)) {
            let value = this[key];

            if (typeof value === 'function')
                continue;
            
            let allowed = Reflect.getMetadata('state-transfer:enabled', this, key);

            if (allowed)
                state[key] = this.deflate(value);
        }

        return state;
    }

    private _hasServerState = false;

    get hasServerState() {
        return this._hasServerState;
    }

    /**
     * Override this to rename the page component based on an input 
     */
    get pageComponentId() {
        return undefined;
    }

    fireInit() {
        
        let fallback = true;
        let pageId = this.pageComponentId;
        let firstLoad = false;

        if (this.__pageService) {
            this.subscribe(this.__pageService.router.events, ev => {
                if (ev instanceof NavigationStart) {
                    if (!firstLoad) {
                        this._hasServerState = false;
                    } 
                    firstLoad = false;
                }
            });
        }
        
        if (this.__pageService && pageId) {
            if (this.__pageService.stateTransfer) {
                if (typeof window === 'undefined') {
                    // Server side

                    this.__pageService.stateTransfer.set(pageId, { works: true });
                    this.__pageService.zone.onStable.subscribe(() => {
                        this.__pageService.stateTransfer.set(pageId, this.getState())
                    });
                } else {
                    // Client side 

                    let data = this.__pageService.stateTransfer.get(pageId, null);

                    if (data) {
                        this.__pageService.stateTransfer.set(pageId, null);
                    }
                    this.restoreState(data);
                }
            } else {
                console.warn('[Page] Server state transfer service not available.');
            }
        }

        super.fireInit();
    }
}